import { createAuthClient } from "better-auth/react";
import { adminClient, inferAdditionalFields, oneTapClient } from "better-auth/client/plugins";
import { API_BASE_URL } from "./api-service";

// Better Auth rejects a relative base ("Invalid base URL"), so build an
// absolute one from the page's own origin. Still same-origin — nginx
// proxies /v1 to the API — just spelled out in full.
const AUTH_BASE_URL = `${window.location.origin}${API_BASE_URL}/auth`;

export const authClient = createAuthClient({
	plugins: [
		adminClient(),
		inferAdditionalFields({
			user: {
				MaCB: {
					type: "string",
					required: false,
				},
				faculty_code: {
					type: "string",
					required: false,
				},
				// Mọi quyền cấp riêng nằm trong một object: `{"export_excel": true}`.
				// Khoá vắng mặt = không có quyền, nên đọc bằng `hasPermission` ở
				// `lib/permissions.ts` thay vì truy thẳng vào khoá.
				permissions: {
					type: "json",
					required: false,
				},
			},
		}),
	],
	baseURL: AUTH_BASE_URL,
	fetchOptions: {
		timeout: 5000,
	},
});

export type User = (typeof authClient.$Infer.Session)["user"];
export type Session = (typeof authClient.$Infer.Session)["session"];

export const { useSession, signIn, signUp, signOut } = authClient;

/**
 * Google One Tap needs its own client instance.
 *
 * `oneTapClient({ clientId })` requires a concrete string at the moment the
 * plugin is built, but this app deliberately has no build-time
 * `VITE_GOOGLE_CLIENT_ID` (see fe-react/Dockerfile) — the ID is only known
 * after fetching `/v1/public/config` at runtime, and Better Auth plugins
 * cannot be attached to `authClient` after it is created. Rather than making
 * the whole app wait on that fetch before `authClient` (used everywhere,
 * synchronously, from module load) becomes available, a second minimal
 * client is built lazily the first time a non-empty client ID shows up. It
 * shares the same `baseURL` (same origin, same cookies) and exists only to
 * drive the One Tap prompt — every other call still goes through the single
 * `authClient` above, so `adminClient()` / `inferAdditionalFields` are
 * untouched.
 */
let oneTapAuthClient: ReturnType<typeof createOneTapAuthClient> | null = null;
let oneTapAuthClientId: string | null = null;

function createOneTapAuthClient(clientId: string) {
	return createAuthClient({
		plugins: [
			oneTapClient({
				clientId,
				autoSelect: false,
				cancelOnTapOutside: true,
				context: "signin",
			}),
		],
		baseURL: AUTH_BASE_URL,
		fetchOptions: {
			timeout: 5000,
		},
	});
}

export function getOneTapAuthClient(clientId: string) {
	if (!clientId) return null;
	if (!oneTapAuthClient || oneTapAuthClientId !== clientId) {
		oneTapAuthClient = createOneTapAuthClient(clientId);
		oneTapAuthClientId = clientId;
	}
	return oneTapAuthClient;
}
